import { makeStyles, StepIconProps } from "@material-ui/core";
import { useIO, useLinked, useModel } from "@type-r/react";
import { CreateWorkflow, div, Flex, getIdFromPath, getQueryParam, Loading, Page, WorkflowProps } from "lqx-react";
import React, { useEffect } from "react";
import { forwardRef, useContext } from "react";
import { RowH1 } from "../../../../../common/inmatch";
import { AssetType } from "../api/assetType";
import { ImportJobDetails } from "../api/ImportJobDetails";
import { IngestionJob } from "../api/ingestionJob";
import { NotificationAlert } from "../components/notification-alert";
import { SignalRContext } from "../page-router";
import { ChooseAssetTypeStep } from "./choose-asset-type-step";
import { CreditNotesWorkflow } from "./creditnotes/credit-notes-workflow";
import { CustomerReferenceDataWorkflow } from "./customer-reference-data/customer-reference-data-workflow";
import { InvoicesWorkflow } from "./invoices/invoices-workflow";
import { MultiAssetWorkflow } from "./multiasset/multiassets-workflow";
import { PaymentsWorkflow } from "./payments/payments-workflow";
import { PolicyWorkflow } from "./policies/policy-workflow";
import { PurchaseOrdersWorkflow } from "./purchase-orders/purchase-orders-workflow";
import { RemittanceAdvicesWorkflow } from "./remittance-advices/remittance-advices-workflow";
import { UnappliedCashesWorkflow } from "./unapplied-cashes/unapplied-cashes-workflow";
import { globalStore } from "api/store";

export const IngestPage = forwardRef((props, ref) => {
    const routeAction = getIdFromPath(3);
    const importJobId = getIdFromPath(4);
    const activeStep = getQueryParam('activeStep');
    const isEdit = routeAction == 'edit';
    const $workflowStep = useLinked(0);
    const $assetType = useLinked(null);
    const ingestionJob = useModel(IngestionJob);
    const importJob = useModel(ImportJobDetails);
    var classes = useStyles();
    const $isAssetsModification = useLinked(false);
    const $doStoreJob = useLinked(false);
    const isStoreJobReady = useIO(
        async () => $doStoreJob.isTruthy && await storeJob(),
        [$doStoreJob.value]
    );

    const $doDeleteJob = useLinked(false);
    const isDeleteJobReady = useIO(
        async () => $doDeleteJob.isTruthy && await deleteJob(),
        [$doDeleteJob.value]
    );

    const $doStartOver = useLinked(false);
    const isStartOverReady = useIO(
        async () => $doStartOver.isTruthy && await startOver(),
        [$doStartOver.value]
    );

    const $refreshEvents = useLinked(false);
    useEffect(() => $refreshEvents.set(false), [$refreshEvents['_changeToken']]);

    const isReady = useIO(
        async () => {
            if (importJobId) {
                importJob.set({ id: importJobId });
                await importJob.fetch().catch(e => console.error(e));
                $workflowStep.set(activeStep ? +activeStep : 0);
                ingestionJob.set({ id: importJob.documentId, importJobId: importJob.id });
                $assetType.set(importJob.assetTypeId);
            }
            if (!globalStore.currenciesLookup.items.models.length) {
                await globalStore.currenciesLookup.fetch();
            }
        }
    );

    const populateImportJobForNewUpload = async (id) => {
        importJob.set({ id: id });
        await importJob.fetch().catch(e => console.error(e));
        $workflowStep.set(2);
        ingestionJob.set({ id: importJob.documentId, importJobId: importJob.id });
        $assetType.set(importJob.assetTypeId);
    }

    const goToNextStep = async (id?, isNew?) => {
        $workflowStep.set($workflowStep.value + 1);
        if (isNew) {
            await populateImportJobForNewUpload(id);
        }
    };

    const goToPreviousStep = () => $workflowStep.set($workflowStep.value - 1);

    const goToFirstStep = () => $workflowStep.set(0);

    const deleteJob = async () => {
        try {
            if (ingestionJob.id) {
                await ingestionJob.destroy({ params: { assetType: $assetType.value } });
            }
            goToFirstStep();
        }
        finally {
            $doDeleteJob.set(false);
        }
    }

    const startOver = async () => {
        try {
            if (ingestionJob.id) {
                await ingestionJob.destroy();
            }
            $workflowStep.set(0);
        }
        finally {
            $doStartOver.set(false);
        }
    }

    const storeJob = async () => {
        try {
            await ingestionJob.save({ params: { assetType: $assetType.value } });
            goToNextStep();
        }
        finally {
            $doStoreJob.set(false);
        }
    }

    const cancel = () => $doDeleteJob.set(true);

    const store = () => $doStoreJob.set(true);

    const doStartOver = () => $doStartOver.set(true);

    const onAssetTypeSelected = assetType => {
        $assetType.set(assetType);
        goToNextStep();
    }

    const isChooseAssetTypeStepEnabled = () => {
        if (isEdit) return false;

        return $workflowStep.value === 0;
    }

    const { notificationFunctions } = useContext(SignalRContext);

    return (isReady && isDeleteJobReady && isStoreJobReady && isStartOverReady ?
        <>
            <Page maxWidth={false} header={
                <Flex center>
                    <RowH1 className={classes.title}>Digitize an Asset</RowH1>
                </Flex>
            }>
                <Flex row>
                    <IngestWorkflowContainer>
                        <IngestWorkflow activeStep={$workflowStep.value} steps={isEdit ? workflowStepsForEdit : workflowStepsForCreate} />
                        <NotificationAlert ref={notificationFunctions} />
                    </IngestWorkflowContainer>
                </Flex>

                {isChooseAssetTypeStepEnabled() && <ChooseAssetTypeStep onAssetTypeSelected={onAssetTypeSelected} cancel={cancel} />}

                {$assetType.value == AssetType.invoice &&
                    <InvoicesWorkflow
                        isEdit={isEdit}
                        isAssetsModification={$isAssetsModification}
                        workflowStep={$workflowStep.value}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={doStartOver}
                        $refreshEvents={$refreshEvents}
                    />}

                {$assetType.value == AssetType.insurancePolicy &&
                    <PolicyWorkflow
                        workflowStep={$workflowStep.value}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        store={store}
                    />}

                {$assetType.value == AssetType.payment &&
                    <PaymentsWorkflow
                        workflowStep={$workflowStep.value}
                        isEdit={isEdit}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={startOver}
                        $refreshEvents={$refreshEvents}
                    />}

                {$assetType.value == AssetType.remittanceAdvice &&
                    <RemittanceAdvicesWorkflow
                        workflowStep={$workflowStep.value}
                        isEdit={isEdit}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={startOver}
                        $refreshEvents={$refreshEvents}
                    />}

                {$assetType.value == AssetType.creditNote &&
                    <CreditNotesWorkflow
                        isEdit={isEdit}
                        workflowStep={$workflowStep.value}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={doStartOver}
                        $refreshEvents={$refreshEvents}
                    />}
                {$assetType.value == AssetType.purchaseOrder &&
                    <PurchaseOrdersWorkflow
                        isEdit={isEdit}
                        workflowStep={$workflowStep.value}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={doStartOver}
                    />}
                {$assetType.value == AssetType.multiAsset &&
                    <MultiAssetWorkflow
                        isEdit={isEdit}
                        workflowStep={$workflowStep.value}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={doStartOver}
                        importTemplateId={importJob.importTemplateId}
                        $refreshEvents={$refreshEvents}
                    />}
                {$assetType.value == AssetType.unappliedCash &&
                    <UnappliedCashesWorkflow
                        workflowStep={$workflowStep.value}
                        isEdit={isEdit}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={startOver}
                        $refreshEvents={$refreshEvents}
                    />}
                {$assetType.value == AssetType.customerReferenceData &&
                    <CustomerReferenceDataWorkflow
                        workflowStep={$workflowStep.value}
                        isEdit={isEdit}
                        job={ingestionJob}
                        next={goToNextStep}
                        back={goToPreviousStep}
                        cancel={cancel}
                        startOver={startOver}
                        $refreshEvents={$refreshEvents}
                    />}
            </Page>
        </>
        : <Loading />
    );
});

const useStyles = makeStyles({
    title: {
        color: '#3d6889'
    }
});

const IngestWorkflowContainer = div({
    maxWidth: '1000px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto'
});

const IngestWorkflow = ({ activeStep, steps }: Partial<WorkflowProps>) => CreateWorkflow({
    activeStep: activeStep,
    steps: steps
});


const WorkflowInBlockIcon = ({ active, completed, ...rest }: Partial<StepIconProps>) => {
    const classes = useWorkflowInBlockIconStyles(rest);
    return <img className={classes.root} src="/assets/images/inblock_logo.png" alt="InBlock Logo" />
}

const useWorkflowInBlockIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: 28,
        height: 28,
    }
});

const workflowStepsForCreate = [
    {
        title: 'CHOOSE ASSET TYPE',
        description: 'Invoice, loan, policy etc.',
    },
    {
        title: 'ADD ASSETS',
        description: 'Select assets to digitize',
    },
    {
        title: 'CONFIRM DATA',
        description: 'Review data before asset is loaded',
    },
    {
        title: 'ASSET SAVED',
        description: 'Decide what to do next with your data',
        icon: WorkflowInBlockIcon
    },
]

const workflowStepsForEdit = [
    {
        title: 'CONFIRM DATA',
        description: 'Review data before asset is loaded',
    },
    {
        title: 'ASSET SAVED',
        description: 'Decide what to do next with your data',
        icon: WorkflowInBlockIcon
    },
]
